/* eslint-disable @typescript-eslint/no-explicit-any */
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './baseQuery';
import { Patch } from 'immer';

interface GetProgramsParams {
  page?: number;
  search?: string;
  university?: string;
  studyLevel?: string;
  courseForm?: string;
  year?: string;
  intake?: string;
  fee?: string;
  min_fee?: number | null;
  max_fee?: number | null;
  major?: string;
  limit?: number;
}

interface GetUniversityProgramsParams {
  universityId: string;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  populate?: boolean;
}

interface FavoriteParams {
  admissionProgramId: string;
  userIds: string[];
}
interface RemoveFavoriteParams {
  admissionProgramId: string;
}

export const programsApi = createApi({
  reducerPath: 'programsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Programs', 'Favorites'],
  endpoints: (builder) => ({
    getPrograms: builder.query({
      query: (params) => {
        const { page = 1, limit, ...filters } = params;
        const queryParts = [`page=${page.toString()}`];

        if (limit) {
          queryParts.push(`limit=${limit.toString()}`);
        }

        (
          Object.entries(filters) as [keyof GetProgramsParams, string][]
        ).forEach(([key, value]) => {
          if (value) {
            const paramKey = key === 'search' ? 'programName' : key;
            // Use encodeURIComponent to ensure spaces are encoded as %20 not +
            queryParts.push(`${paramKey}=${encodeURIComponent(value)}`);
          }
        });

        return {
          url: `admission-programs/with-filters?${queryParts.join('&')}`,
          method: 'GET'
        };
      },
      providesTags: ['Programs']
    }),
    getProgram: builder.query({
      query: (id) => ({
        url: `admission-programs/${id}`,
        method: 'GET'
      }),
      providesTags: ['Programs']
    }),
    getUniversityPrograms: builder.query({
      query: (params: GetUniversityProgramsParams) => {
        const {
          universityId,
          limit = 200,
          sortBy = 'name',
          sortOrder = 'asc',
          populate = true
        } = params;
        const queryParams = new URLSearchParams();

        queryParams.append('university_id', universityId);
        queryParams.append('limit', limit.toString());
        queryParams.append('sortBy', sortBy);
        queryParams.append('sortOrder', sortOrder);
        queryParams.append('populate', populate.toString());

        return {
          // url: `programs/university/${universityId}?${queryParams.toString()}`,
          url: `programs/?${queryParams.toString()}`,
          method: 'GET'
        };
      },
      providesTags: ['Programs']
    }),
    getFavoritePrograms: builder.query({
      query: ({ page = 1, limit = 10 }) => ({
        url: `admission-programs/user/favorites?page=${page}&limit=${limit}`,
        method: 'GET'
      }),
      serializeQueryArgs: () => {
        // Use a consistent cache key for all pages
        return 'favoritePrograms';
      },
      merge: (currentCache, newItems, { arg }) => {
        if (arg.page === 1) {
          // First page, replace the cache
          return newItems;
        }
        // Subsequent pages, append to existing cache
        if (currentCache?.data && newItems?.data) {
          return {
            ...newItems,
            data: [...currentCache.data, ...newItems.data]
          };
        }
        return newItems;
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        // Force refetch if page changes
        return currentArg?.page !== previousArg?.page;
      },
      providesTags: ['Favorites']
    }),
    toggleFavorite: builder.mutation<void, FavoriteParams>({
      query: ({ admissionProgramId, userIds }) => ({
        url: `/admission-programs/${admissionProgramId}/favorites`,
        method: 'POST',
        body: {
          favouriteBy: userIds
        },
        headers: {
          'Content-Type': 'application/json'
        }
      }),
      invalidatesTags: ['Favorites'],
      async onQueryStarted(
        { admissionProgramId, userIds },
        { dispatch, queryFulfilled, getState }
      ) {
        // Optimistic update for getPrograms - update all cache entries
        const patchResults: {
          patches: Patch[];
          inversePatches: Patch[];
          undo: () => void;
        }[] = [];
        const cacheEntries = (getState() as any).programsApi?.queries;

        if (cacheEntries) {
          Object.keys(cacheEntries).forEach((queryKey) => {
            if (queryKey.startsWith('getPrograms')) {
              const cacheEntry = cacheEntries[queryKey];
              if (cacheEntry?.data) {
                const patchResult = dispatch(
                  programsApi.util.updateQueryData(
                    'getPrograms',
                    cacheEntry.originalArgs,
                    (draft) => {
                      if (draft?.docs) {
                        const program = draft.docs.find(
                          (p: any) => p._id === admissionProgramId
                        );
                        if (program) {
                          if (!program.favouriteBy) {
                            program.favouriteBy = [];
                          }
                          // Add all userIds to favorites
                          userIds.forEach((userId) => {
                            if (!program.favouriteBy.includes(userId)) {
                              program.favouriteBy.push(userId);
                            }
                          });
                        }
                      }
                    }
                  )
                );
                patchResults.push(patchResult);
              }
            }
          });
        }

        // Optimistic update for getFavoritePrograms
        const patchResult2 = dispatch(
          programsApi.util.updateQueryData(
            'getFavoritePrograms',
            undefined,
            (draft) => {
              if (draft?.data) {
                // Add the program to favorites list if not already present
                const existingProgram = draft.data.find(
                  (p: any) => p._id === admissionProgramId
                );
                if (!existingProgram) {
                  // Get the program data from getPrograms cache
                  const programsCache = (getState() as any).programsApi
                    ?.queries;
                  const programData = programsCache?.data?.find(
                    (p: any) => p._id === admissionProgramId
                  );
                  if (programData) {
                    draft.data.push(programData);
                  }
                }
              }
            }
          )
        );

        try {
          await queryFulfilled;
        } catch {
          // Revert on error
          patchResults.forEach((patchResult) => patchResult.undo());
          patchResult2.undo();
        }
      }
    }),
    removeFavoriteProgram: builder.mutation<void, RemoveFavoriteParams>({
      query: ({ admissionProgramId }) => ({
        url: `/admission-programs/${admissionProgramId}/favorites`,
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      }),
      invalidatesTags: ['Favorites'],
      async onQueryStarted(
        { admissionProgramId },
        { dispatch, queryFulfilled, getState }
      ) {
        // Optimistic update for getPrograms - update all cache entries
        const patchResults: {
          patches: Patch[];
          inversePatches: Patch[];
          undo: () => void;
        }[] = [];
        const cacheEntries = (getState() as any).programsApi?.queries;

        if (cacheEntries) {
          Object.keys(cacheEntries).forEach((queryKey) => {
            if (queryKey.startsWith('getPrograms')) {
              const cacheEntry = cacheEntries[queryKey];
              if (cacheEntry?.data) {
                const patchResult = dispatch(
                  programsApi.util.updateQueryData(
                    'getPrograms',
                    cacheEntry.originalArgs,
                    (draft) => {
                      if (draft?.docs) {
                        const program = draft.docs.find(
                          (p: any) => p._id === admissionProgramId
                        );
                        if (program?.favouriteBy) {
                          const userId = (getState() as any).auth?.user?._id;
                          if (userId) {
                            program.favouriteBy = program.favouriteBy.filter(
                              (id: string) => id !== userId
                            );
                          }
                        }
                      }
                    }
                  )
                );
                patchResults.push(patchResult);
              }
            }
          });
        }

        // Optimistic update for getFavoritePrograms
        const patchResult2 = dispatch(
          programsApi.util.updateQueryData(
            'getFavoritePrograms',
            undefined,
            (draft) => {
              if (draft?.data) {
                // Remove the program from favorites list
                draft.data = draft.data.filter(
                  (p: any) => p._id !== admissionProgramId
                );
              }
            }
          )
        );

        try {
          await queryFulfilled;
        } catch {
          // Revert on error
          patchResults.forEach((patchResult) => patchResult.undo());
          patchResult2.undo();
        }
      }
    })
  })
});

export const {
  useGetProgramsQuery,
  useGetProgramQuery,
  useGetUniversityProgramsQuery,
  useToggleFavoriteMutation,
  useGetFavoriteProgramsQuery,
  useRemoveFavoriteProgramMutation
} = programsApi;
